import {
  Box,
  ButtonBase,
  Drawer,
  FormControlLabel,
  List,
  ListItem,
  Typography
} from '@mui/material';
import { Table } from '@tanstack/react-table';

import Checkbox from '@/components/Checkbox';
import EllipsisWithTooltip from '@/components/EllipsisWithTooltip';
import { useFilter } from '@/pages/Catalog/contexts/filter/useFilter';
import { CatalogItem } from '@/pages/Catalog/types';
import getAmountLabel from '@/utils/getAmountLabel';
import { transformCamelCaseToSpaces } from '@/utils/transformCamelCaseToSpaces';

import { FILTER_DRAWER_WIDTH } from '../constants';
import useTableFilters from './useTableFilters';

const buttonBaseStyle = { color: 'primary.main', px: 0.2, textDecoration: 'underline' };

const TableFilters = ({
  filterOpen,
  setFilterOpen,
  table,
  expandedData,
  height
}: {
  filterOpen: boolean;
  setFilterOpen: (newFilterOpen: boolean) => void;
  table: Table<CatalogItem>;
  expandedData: boolean;
  height: string | number;
}) => {
  const { columnFiltersWithCounts } = useTableFilters(table);
  const { filters, clearAllFilters, setFilter } = useFilter();

  return (
    <Drawer
      anchor='left'
      open={filterOpen}
      sx={{
        zIndex: 2,
        height,
        flexShrink: 0,
        fontSize: 14,
        '& .MuiDrawer-paper': {
          px: 2,
          py: 1,
          borderRight: 'none'
        }
      }}
      variant='persistent'
      onClose={() => {
        setFilterOpen(false);
      }}
    >
      <Box
        sx={{
          width: FILTER_DRAWER_WIDTH - 32,
          border: '1px solid',
          borderColor: 'text.primary',
          height: '100%',
          p: 1.5,
          pr: 1,
          backgroundColor: 'secondary.main',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, mr: 0.5 }}>
          <ButtonBase
            sx={buttonBaseStyle}
            onClick={clearAllFilters}
          >
            Clear all
          </ButtonBase>
        </Box>
        <Box sx={{ height: 'calc(100% - 88px)', overflow: 'auto' }}>
          <List disablePadding>
            {Object.keys(columnFiltersWithCounts).map(column => (
              <ListItem
                key={column}
                sx={{
                  backgroundColor: 'background.paper',
                  fontSize: '14px',
                  fontWeight: 600,
                  display: 'flex',
                  flexDirection: 'column',
                  p: 0,
                  '&:not(:last-child)': { mb: 2 }
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    borderBottom: '1px solid',
                    borderBottomColor: 'text.primary',
                    px: 1,
                    pt: 1.5,
                    pb: 0.5
                  }}
                >
                  {transformCamelCaseToSpaces(column)}
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    p: 1,
                    display: 'flex',
                    alignItems: 'flex-start',
                    flexDirection: 'column'
                  }}
                >
                  {columnFiltersWithCounts[column].map(({ label, count }) => {
                    // @ts-expect-error filter exist
                    const current = filters[column];
                    return (
                      <FormControlLabel
                        key={label}
                        control={
                          <Checkbox
                            checked={current === label}
                            onChange={() => {
                              setFilter(column, label);
                            }}
                          />
                        }
                        label={
                          <Box sx={{ maxWidth: 109, display: 'flex', alignItems: 'center' }}>
                            <EllipsisWithTooltip text={label} />
                            <Typography ml={0.5}>({count.toLocaleString('en-US')})</Typography>
                          </Box>
                        }
                        sx={{ ml: 0 }}
                      />
                    );
                  })}
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'background.paper',
            p: 1,
            mt: 2,
            mb: 1.5,
            mr: 0.5
          }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: 'primary.main',
              mr: 1
            }}
          />
          {getAmountLabel(table.getRowModel().rows.length, expandedData ? 'item' : 'code')}
        </Box>
      </Box>
    </Drawer>
  );
};

export default TableFilters;
